import { WidgetAutocheck } from "@redotech/redo-storefront-widget-common/widget";
import { DEFAULT_REDO_OVERLAY_Z_INDEX } from "@redotech/redo-web/shopify/shopify-z-index";
import { isLoggedInToStorefront } from "@redotech/redo-web/utils/storefront-is-logged-in";
import { memo } from "react";
import { createRoot } from "react-dom/client";
import { AliaEventListener } from "../alia/alia-event-listener";
import { StorefrontClient } from "../client/storefront";
import { LocationClientProvider } from "../location/location-client-context";
import { ExtensionShopifyClientContext } from "../shopify";
import { CheckoutProbabilityContextProvider } from "./checkout-probability/checkout-probability-context";
import { CheckoutProbabilityTracker } from "./checkout-probability/checkout-probability-tracker";
import { IntentActiveCartAttribute } from "./checkout-probability/intent-active-cart-attribute";
import { RescuePanel } from "./checkout-probability/rescue-panel";
import { PricingTestTracker } from "./conversion/pricing-test/pricing-test-tracker";
import { ShopperIntentTracker } from "./conversion/shopper-intent-tracker";
import { useSplitStorefrontFeatures } from "./conversion/use-split-storefront-features";
import { DeliveryEstimateInitializer } from "./delivery-estimate/delivery-estimate-initializer";
import { HsaCartInitializer } from "./hsa/hsa-cart-initializer";
import { HsaPdpInitializer } from "./hsa/hsa-pdp-initializer";
import { LoyaltyStorefront } from "./marketing/loyalty/storefront";
import { ProductBackInStockStorefront } from "./marketing/product-back-in-stock/storefront";
import { PackageProtectionExposureTracker } from "./package-protection-exposure-tracker";
import { RedoStorefrontContainer } from "./redo-storefront-container";
import { RedoStorefrontOverlayWrapper } from "./redo-storefront-overlay-wrapper";
import { ReviewSurvey } from "./reviews/reviews-survey";
import { SaveForLaterSlideInPanel } from "./save-for-later/save-for-later-slide-in-panel";
import "./styles.css";
import "./styles.global.css";

// also referenced in "./styles.global.css"
export const REDO_STOREFRONT_OVERLAY_CONTAINER = "redo-storefront-container";

const RedoStorefrontOverlay = memo(function RedoStorefrontOverlay({
  autocheck,
  storefrontExtensionShopifyClient,
}: {
  autocheck: WidgetAutocheck;
  storefrontExtensionShopifyClient: StorefrontClient;
}) {
  useSplitStorefrontFeatures({ autocheck, storefrontExtensionShopifyClient });

  const shouldShowSaveForLaterSlideInPanel =
    autocheck.customerAccounts.enabled &&
    autocheck.customerAccounts.saveForLaterPanel?.enabled &&
    !isLoggedInToStorefront();

  const storeUrl =
    globalThis.redoStorefront?.storeUrl ?? globalThis.Shopify?.shop;
  if (!storeUrl) {
    console.error("storeUrl is not set");
    return null;
  }

  return (
    <RedoStorefrontOverlayWrapper
      extensionShopifyClient={storefrontExtensionShopifyClient}
    >
      <PackageProtectionExposureTracker />
      {shouldShowSaveForLaterSlideInPanel && (
        <SaveForLaterSlideInPanel
          saveForLaterMarketingEnabled={
            (autocheck.marketing?.enabled &&
              autocheck.marketing?.subscribeFromSaveForLater) ??
            false
          }
          saveForLaterPanelSettings={
            autocheck.customerAccounts.saveForLaterPanel
          }
        />
      )}
      {autocheck.reviews.enabled && <ReviewSurvey />}
      {(autocheck.conversion.enabled || autocheck.concierge.enabled) && (
        <ShopperIntentTracker />
      )}
      {autocheck.conversion.enabled &&
        autocheck.conversion.deliveryEstimate.enabled && (
          <DeliveryEstimateInitializer
            autocheck={autocheck}
            deliveryEstimateSettings={autocheck.conversion.deliveryEstimate}
          />
        )}
      {autocheck.hsa.enabled && autocheck.hsa.pdpWidgetPlacement && (
        <HsaPdpInitializer
          placement={autocheck.hsa.pdpWidgetPlacement}
          storefrontClient={storefrontExtensionShopifyClient}
        />
      )}
      {autocheck.hsa.enabled &&
        !!autocheck.hsa.cartWidgetPlacements?.length && (
          <HsaCartInitializer
            placements={autocheck.hsa.cartWidgetPlacements}
            storefrontClient={storefrontExtensionShopifyClient}
          />
        )}
      {autocheck.conversion.enabled && autocheck.conversion.pricingTest && (
        <PricingTestTracker
          autocheckPricingTest={autocheck.conversion.pricingTest}
          currency={(globalThis as any).Shopify?.currency?.active ?? "USD"}
          storefrontClient={storefrontExtensionShopifyClient}
        />
      )}
      {autocheck.loyalty.enabled && (
        <LoyaltyStorefront
          loyaltySettings={autocheck.loyalty}
          storeUrl={storeUrl}
        />
      )}
      {autocheck.marketing?.enabled && (
        <>
          <ProductBackInStockStorefront autocheck={autocheck} />
          <AliaEventListener />
        </>
      )}
      {autocheck.conversion.enabled &&
        autocheck.conversion.predictiveConversion.enabled && (
          <LocationClientProvider>
            <CheckoutProbabilityContextProvider
              hasActiveModel={
                !!autocheck.conversion.predictiveConversion
                  .activeCheckoutHazardModel
              }
            >
              <IntentActiveCartAttribute
                storefrontClient={storefrontExtensionShopifyClient}
              />
              <CheckoutProbabilityTracker
                shouldWriteLineItemProperty={
                  autocheck.conversion.predictiveConversion
                    .activeCheckoutTreeHasCheckoutProbabilitySegment ||
                  !!autocheck.packageProtection.coverageMlModel
                }
              />
              <RescuePanel
                allowEmptyCart={
                  autocheck.conversion.predictiveConversion.rescueModal
                    .configuration.allowEmptyCart
                }
                autocheck={autocheck}
                autocheckRescuePanelSplit={
                  autocheck.conversion.predictiveConversion.rescueModal
                    .activeSplit
                }
                discount={
                  autocheck.conversion.predictiveConversion.rescueModal
                    .configuration.discount
                }
                discountCode={
                  autocheck.conversion.predictiveConversion.rescueModal
                    .configuration.discountCode
                }
                dropoffPercentageTrigger={
                  autocheck.conversion.predictiveConversion.rescueModal
                    .configuration.dropoffPercentageTrigger
                }
                hasActiveModel={
                  !!autocheck.conversion.predictiveConversion
                    .activeCheckoutHazardModel
                }
                rescueModalUi={
                  autocheck.conversion.predictiveConversion.rescueModal
                    ?.configuration.ui
                }
                secondsInColdTrigger={
                  autocheck.conversion.predictiveConversion.rescueModal
                    .configuration.secondsInColdTrigger
                }
                stalePageMinCartItems={
                  autocheck.conversion.predictiveConversion.rescueModal
                    .configuration.stalePageMinCartItems
                }
                storefrontClient={storefrontExtensionShopifyClient}
              />
            </CheckoutProbabilityContextProvider>
          </LocationClientProvider>
        )}
    </RedoStorefrontOverlayWrapper>
  );
});

export function initialize({
  autocheck,
  storefrontExtensionShopifyClient,
}: {
  autocheck: WidgetAutocheck;
  storefrontExtensionShopifyClient: StorefrontClient;
}) {
  if (globalThis.blockRedo) {
    return;
  }

  if (!document.getElementById(REDO_STOREFRONT_OVERLAY_CONTAINER)) {
    const redoContainer = document.createElement("div");
    redoContainer.id = REDO_STOREFRONT_OVERLAY_CONTAINER;
    redoContainer.style.zIndex = `${DEFAULT_REDO_OVERLAY_Z_INDEX}`;
    document.body.append(redoContainer);

    const root = createRoot(redoContainer);

    root.render(
      <RedoStorefrontContainer container={redoContainer}>
        <ExtensionShopifyClientContext.Provider
          value={storefrontExtensionShopifyClient}
        >
          <RedoStorefrontOverlay
            autocheck={autocheck}
            storefrontExtensionShopifyClient={storefrontExtensionShopifyClient}
          />
        </ExtensionShopifyClientContext.Provider>
      </RedoStorefrontContainer>,
    );
  }
}
